import { Link } from 'react-router-dom';
import { Box, List, ListItem } from "@mui/material";
import { routes } from '../../constants';
import MeasureSection from './MeasureSection';
import { components } from '../../api/types';

type Quantiles = components['schemas']['Quantiles'] | null;

type Props = {
  /* Measure data */
  measureNames: string[],
  measureCorrelationPercents: number[],
  predictedScores: number[],
  predictedStds: number[],
  predictedPercentileRanks: (number | null)[],

  /* Diagnosis data */
  diagnosisName1: string,
  diagnosisName2: string,
  diagnosisColor1: string,
  diagnosisColor2: string,
  quantiles1: Quantiles[],
  quantiles2: Quantiles[],
}

/**
 * JSX Component containing proposed measures for a given diagnosis validation
 * 
 * @param measures: Measure[] - list of proposed measures
 * 
 * @returns Diagnosis details element
 */
const MeasureList = ({
  /* Measure data */
  measureNames,
  measureCorrelationPercents,
  predictedScores,
  predictedStds,
  predictedPercentileRanks,

  /* Diagnosis data */
  diagnosisName1,
  diagnosisName2,
  diagnosisColor1,
  diagnosisColor2,
  quantiles1,
  quantiles2,
}: Props): JSX.Element => {
  
  const limit = Math.min(3, measureNames.length);

  // TODO: Make this grab the top <n> indices, for simplicity, then map over them in the return statement
  const sortedIndices = measureCorrelationPercents.map((_, i) => i).sort((a, b) => {
    return measureCorrelationPercents[b] - measureCorrelationPercents[a];
  });

  return (
    <div className="diagnosisCategory">
      {
        measureNames.length
        ?
          <Box className="diagnosisCategory">
            <List className="text content measuresList">
              {Array.from({ length: limit }, (_, i) => (
                <ListItem key={i}>
                  <Box>
                    <MeasureSection
                      measureName={measureNames[sortedIndices[i]]}
                      measureCorrelationPercent={measureCorrelationPercents[sortedIndices[i]]}
                      predictedScore={predictedScores[sortedIndices[i]]}
                      predictedStd={predictedStds[sortedIndices[i]]}
                      predictedPercentileRank={predictedPercentileRanks[sortedIndices[i]]}
                      diagnosisName1={diagnosisName1}
                      diagnosisName2={diagnosisName2}
                      diagnosisColor1={diagnosisColor1}
                      diagnosisColor2={diagnosisColor2}
                      quantiles1={quantiles1[sortedIndices[i]]}
                      quantiles2={quantiles2[sortedIndices[i]]}
                    />
                  </Box>
                </ListItem>
              ))}
            </List>
          </Box>
        :
          <div className="text content">
            <p>The client has already taken all relevant tests. Consider additional means of confirming the diagnosis based on the DSM-5 criteria for the selected diagnoses.</p>
            
            {/* Diagnosis 1 */}
            {
              diagnosisName1 !== 'No Diagnosis' &&
              <p style={{color: diagnosisColor1}}>
                { diagnosisName1 }
                <Link
                  to={routes.DsmAssociations.path(diagnosisName1)}
                  title={`View DSM-5 criteria and mapped measures for ${diagnosisName1}`}
                  aria-label={`View DSM-5 criteria and mapped measures for ${diagnosisName1}`}
                  style={{
                    display: 'inline-block',
                    marginLeft: '10px',
                    padding: '2px 10px',
                    borderRadius: '999px',
                    background: '#eef2ff',
                    color: '#1e40af',
                    border: '1px solid #c7d2fe',
                    textDecoration: 'none',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    verticalAlign: 'middle',
                  }}
                >
                  DSM-5 Criteria & Measures
                </Link>
              </p>
            }
            
            {/* Diagnosis 2 */}
            {
              diagnosisName2 !== 'No Diagnosis' &&
              <p style={{color: diagnosisColor2}}>
                { diagnosisName2 }
                <Link
                  to={routes.DsmAssociations.path(diagnosisName2)}
                  title={`View DSM-5 criteria and mapped measures for ${diagnosisName2}`}
                  aria-label={`View DSM-5 criteria and mapped measures for ${diagnosisName2}`}
                  style={{
                    display: 'inline-block',
                    marginLeft: '10px',
                    padding: '2px 10px',
                    borderRadius: '999px',
                    background: '#eef2ff',
                    color: '#1e40af',
                    border: '1px solid #c7d2fe',
                    textDecoration: 'none',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    verticalAlign: 'middle',
                  }}
                >
                  DSM-5 Criteria & Measures
                </Link>
              </p>
            }
          </div>
      }
    </div>
  );





  return (
    <>
      <Box className="diagnosisCategory">
        <List className="text content measuresList">
          {Array.from({ length: limit }, (_, i) => (
            <ListItem key={i}>
              <Box>
                <MeasureSection
                  measureName={measureNames[i]}
                  measureCorrelationPercent={measureCorrelationPercents[i]}
                  predictedScore={predictedScores[i]}
                  predictedStd={predictedStds[i]}
                  predictedPercentileRank={predictedPercentileRanks[i]}
                  diagnosisName1={diagnosisName1}
                  diagnosisName2={diagnosisName2}
                  diagnosisColor1={diagnosisColor1}
                  diagnosisColor2={diagnosisColor2}
                  quantiles1={quantiles1[i]}
                  quantiles2={quantiles2[i]}
                />
              </Box>
            </ListItem>
          ))}
        </List>
      </Box>
    </>
  );
};

export default MeasureList;
